import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import { RetellWebClient } from "retell-client-js-sdk";
import GoogleAuth from "./GoogleAuth.tsx";

interface RegisterCallResponse {
  access_token: string;
}

interface CallHistoryItem {
  id: string;
  call_id: string;
  agent_id: string;
  agent_name: string;
  user_name: string;
  user_email: string;
  call_date_time: string;
  duration_ms: number;
  status: string;
  evaluation_score: number;
  evaluation_report_path: string;
  evaluation_report_object_key?: string;
  recording_url: string;
}

interface CallHistoryResponse {
  success: boolean;
  items: CallHistoryItem[];
  count: number;
  has_more: boolean;
}

const AGENTS = [
  { name: "REITs & InvITs PMS Product Evaluator", value: "agent_51ead5ee790e910c0ddc76018c" }
];

// Admin emails who can see all call history
const ADMIN_EMAILS = [
  "anurag.gaur@altinvest.ai",
  "hashim.khan@propertyshare.in",
  "kunal.moktan@propertyshare.in",
  "rahul.jain@altcapital.ai",
  "apurva.sinha@altinvest.ai"
];

const retellWebClient = new RetellWebClient();

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem("isAuthenticated") === "true"
  );
  const [userProfile, setUserProfile] = useState<{ name: string; email: string; imageUrl?: string } | null>(null);
  const [loginError, setLoginError] = useState("");
  const [isCalling, setIsCalling] = useState(false);
  const [agentId, setAgentId] = useState("");
  const [status, setStatus] = useState("");
  const [callHistory, setCallHistory] = useState<CallHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const itemsPerPage = 10;

  const audioContextRef = useRef<AudioContext | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const localVolumeAnalyzerRef = useRef<AnalyserNode | null>(null);

  useEffect(() => {
    if (sessionStorage.getItem("isAuthenticated") === "true") {
      setIsAuthenticated(true);
      const profile = {
        name: sessionStorage.getItem("name") || "",
        email: sessionStorage.getItem("email") || "",
        imageUrl: sessionStorage.getItem("imageUrl") || undefined,
      };
      setUserProfile(profile);
      fetchCallHistory(profile.email);
    }
  }, []);

  useEffect(() => {
    const handleCallEnded = () => {
      setIsCalling(false);
      setStatus("Call ended by agent.");
    };
    retellWebClient.on("call_ended", handleCallEnded);
    return () => {
      retellWebClient.off("call_ended", handleCallEnded);
    };
  }, []);

  const fetchCallHistory = async (userEmail: string) => {
    setLoadingHistory(true);
    setHistoryError("");
    
    try {
      const isAdmin = ADMIN_EMAILS.includes(userEmail);
      const payload = isAdmin 
        ? {
            action: "get_all_history",
            limit: 100,
            format: "json"
          }
        : {
            action: "get_history",
            user_email: userEmail,
            limit: 100,
            format: "json"
          };

      const response = await fetch("https://glfmexlcqbizd3wjk3pcgsyduq0iosso.lambda-url.ap-south-1.on.aws/call-history", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: CallHistoryResponse = await response.json();
      
      if (data.success) {
        setCallHistory(data.items || []);
        setHasMore(data.has_more || false);
      } else {
        throw new Error("Failed to fetch call history");
      }
    } catch (error: any) {
      setHistoryError(error.message || "Failed to load call history");
      setCallHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  async function requestMicrophone() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("getUserMedia is not supported in this browser. Please use a modern browser like Chrome, Firefox, or Edge.");
      return false;
    }
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const audioContext = audioContextRef.current;
      localStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000,
          channelCount: 1,
        }
      });
      const source = audioContext.createMediaStreamSource(localStreamRef.current);
      localVolumeAnalyzerRef.current = audioContext.createAnalyser();
      localVolumeAnalyzerRef.current.fftSize = 256;
      source.connect(localVolumeAnalyzerRef.current);
      return true;
    } catch (error: any) {
      alert('Microphone access denied or unavailable');
      return false;
    }
  }

  const handleGoogleSuccess = (profile: { name: string; email: string; imageUrl?: string }) => {
    setIsAuthenticated(true);
    setUserProfile(profile);
    sessionStorage.setItem("isAuthenticated", "true");
    sessionStorage.setItem("name", profile.name);
    sessionStorage.setItem("email", profile.email);
    if (profile.imageUrl) sessionStorage.setItem("imageUrl", profile.imageUrl);
    setLoginError("");
    fetchCallHistory(profile.email);
  };

  const handleGoogleFailure = (error: any) => {
    if (typeof error === "string") {
      setLoginError(error || "Google authentication failed. Please try again.");
    } else if (error && error.message) {
      setLoginError(error.message || "Google authentication failed. Please try again.");
    } else {
      setLoginError("Google authentication failed. Please try again.");
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    setIsAuthenticated(false);
    setUserProfile(null);
    setAgentId("");
    setStatus("");
    setCallHistory([]);
    setHistoryError("");
  };

  const handleDownloadReport = async (objectKey: string) => {
    if (!objectKey) return;

    try {
      const response = await fetch("https://glfmexlcqbizd3wjk3pcgsyduq0iosso.lambda-url.ap-south-1.on.aws/call-history", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "get_presigned_url",
          object_key: objectKey,
          expiration: 3600
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.url) {
        window.open(data.url, "_blank");
      } else {
        alert("Failed to get report download link");
      }
    } catch (error: any) {
      console.error("Error fetching presigned URL:", error);
      alert("Error getting report download link");
    }
  };

  const handlePlayRecording = (recordingUrl: string) => {
    if (recordingUrl) {
      window.open(recordingUrl, "_blank");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container" style={{ maxWidth: 400, marginTop: 60 }}>
        <div className="card shadow p-4">
          <h2 className="mb-4 text-center">Login to AI Evaluator</h2>
          <div className="mb-4 text-center">
            <GoogleAuth onSuccess={handleGoogleSuccess} onFailure={handleGoogleFailure} />
          </div>
          {loginError && (
            <div className="alert alert-danger mt-3" role="alert">
              {loginError}
            </div>
          )}
        </div>
      </div>
    );
  }

  async function registerCall(agentId: string): Promise<RegisterCallResponse> {
    try {
      // WARNING: Exposing API keys in frontend is insecure!
      const apiKey = "key_a983a3cfdb464308826bd2bd2d99"; // <-- Replace with your key for testing only
      const response = await fetch("https://api.retellai.com/v2/create-web-call", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + apiKey
        },
        body: JSON.stringify({
          agent_id: agentId,
          retell_llm_dynamic_variables: {
            advisor_name: userProfile?.name || "",
            advisor_email: userProfile?.email || ""
          }
        })
      });
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      const data: RegisterCallResponse = await response.json();
      return data;
    } catch (err: any) {
      setStatus("Error: " + err.message);
      throw new Error(err);
    }
  }

  const toggleConversation = async () => {
    if (!agentId) {
      setStatus("Please select an Agent before starting the call.");
      return;
    }
    setStatus("");
    if (!isCalling) {
      setStatus("Requesting microphone access...");
      const micGranted = await requestMicrophone();
      if (!micGranted) return;
    }
    if (isCalling) {
      retellWebClient.stopCall();
      setIsCalling(false);
      setStatus("");
    } else {
      setStatus("Calling API...");
      const registerCallResponse = await registerCall(agentId);
      if (registerCallResponse.access_token) {
        retellWebClient
          .startCall({
            accessToken: registerCallResponse.access_token,
          })
          .catch(console.error);
        setIsCalling(true);
        setStatus("Call started!");
      }
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return dateTimeString;
    }
  };

  const formatDuration = (durationMs: number) => {
    if (!durationMs || durationMs <= 0) return '-';
    const minutes = Math.round(durationMs / 60000);
    return `${minutes} min${minutes !== 1 ? 's' : ''}`;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'badge bg-success';
      case 'processing': return 'badge bg-warning';
      case 'failed': return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  };

  const handleRefreshHistory = () => {
    if (userProfile?.email) {
      fetchCallHistory(userProfile.email);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(callHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = callHistory.slice(startIndex, endIndex);

  return (
    <div className="container" style={{ maxWidth: 1200, marginTop: 60 }}>
      {/* AI Evaluator Card */}
      <div className="row mb-4">
        <div className="col-md-6 mx-auto">
          <div className="card shadow p-4 position-relative">
            {/* Logout icon button in top right */}
            <button
              className="btn btn-link position-absolute"
              style={{ top: 12, right: 12, color: "#dc3545" }}
              onClick={handleLogout}
              aria-label="Logout"
            >
              {/* SVG logout icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16">
                <path d="M6.5 1a.5.5 0 0 1 .5.5v1A.5.5 0 0 1 6.5 3h-2A1.5 1.5 0 0 0 3 4.5v7A1.5 1.5 0 0 0 4.5 13h2a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-2A2.5 2.5 0 0 1 2 11.5v-7A2.5 2.5 0 0 1 4.5 2h2zm4.854 4.646a.5.5 0 0 0-.708.708L11.293 7H7.5a.5.5 0 0 0 0 1h3.793l-1.147 1.146a.5.5 0 0 0 .708.708l2-2a.5.5 0 0 0 0-.708l-2-2z"/>
              </svg>
            </button>
            <h2 className="mb-4 text-center">AI Evaluator</h2>
            <form onSubmit={e => { e.preventDefault(); toggleConversation(); }}>
              <div className="mb-3">
                <label htmlFor="agentId" className="form-label">Select Evaluator</label>
                <select
                  className="form-select"
                  id="agentId"
                  value={agentId}
                  onChange={e => setAgentId(e.target.value)}
                  disabled={isCalling}
                  required
                >
                  <option value="">-- Select an Agent --</option>
                  {AGENTS.map(agent => (
                    <option key={agent.value} value={agent.value}>{agent.name}</option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                className={`btn ${isCalling ? "btn-danger" : "btn-primary"} w-100`}
                disabled={!agentId}
              >
                {isCalling ? "Stop" : "Start"}
              </button>
            </form>
            {status && (
              <div className="alert alert-info mt-4" role="alert">
                {status}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Call History Table */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="mb-0">Call History</h3>
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={handleRefreshHistory}
                disabled={loadingHistory}
              >
                {loadingHistory ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-clockwise me-2" viewBox="0 0 16 16">
                      <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                      <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                    </svg>
                    Refresh
                  </>
                )}
              </button>
            </div>
            <div className="card-body">
              {historyError && (
                <div className="alert alert-danger" role="alert">
                  {historyError}
                </div>
              )}
              
              {loadingHistory ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading call history...</p>
                </div>
              ) : callHistory.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No call history found.</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th>Date & Time</th>
                          <th>User</th>
                          <th>Email</th>
                          <th>Agent</th>
                          <th>Duration</th>
                          <th>Status</th>
                          <th>Score</th>
                          <th>Report</th>
                          <th>Recording</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <small>{formatDateTime(item.call_date_time)}</small>
                            </td>
                            <td>{item.user_name}</td>
                            <td>
                              <small className="text-muted">{item.user_email}</small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {item.agent_name || 'Unknown Agent'}
                              </small>
                            </td>
                            <td>
                              <small>{formatDuration(item.duration_ms)}</small>
                            </td>
                            <td>
                              <span className={getStatusBadgeClass(item.status)}>
                                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                              </span>
                            </td>
                            <td>
                              {item.evaluation_score > 0 ? (
                                <span className="fw-bold">{item.evaluation_score}</span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {item.evaluation_report_object_key ? (
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleDownloadReport(item.evaluation_report_object_key!)}
                                  title="Download PDF Report"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    className="bi bi-file-earmark-pdf"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
                                    <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.135.968z"/>
                                  </svg>
                                </button>
                              ) : item.evaluation_report_path && item.evaluation_report_path.includes("emailed_to_") ? (
                                <span className="badge bg-info">Emailed</span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {item.recording_url ? (
                                <button
                                  className="btn btn-outline-success btn-sm"
                                  onClick={() => handlePlayRecording(item.recording_url)}
                                  title="Play Recording"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    className="bi bi-play-circle"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                                  </svg>
                                </button>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <nav aria-label="Call history pagination">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, index) => (
                          <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => setCurrentPage(index + 1)}
                            >
                              {index + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                  
                  <div className="text-center mt-3">
                    <small className="text-muted">
                      Showing {startIndex + 1} to {Math.min(endIndex, callHistory.length)} of {callHistory.length} entries
                      {ADMIN_EMAILS.includes(userProfile?.email || '') && (
                        <span className="badge bg-warning ms-2">Admin View - All Calls</span>
                      )}
                    </small>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;